import { Alert, Form, Select, Space } from 'antd'
import type { getPlanningSources } from 'features/libra/inspectionPlanning/capturePlanningSource'
import { FC } from 'react'
import type { GeckoUnit } from 'types'

export interface TicketCreationFieldsProps {
  configurationError?: string
  sourceError?: string
  canCapturePlanningSource: boolean
  areSourcesLoading: boolean
  hasSourceLoadError: boolean
  selectedUnitIds: ReadonlyArray<string>
  sourcesByUnit: ReadonlyMap<string, ReturnType<typeof getPlanningSources>>
  unitMap: ReadonlyMap<string, GeckoUnit>
  onSourceChange: () => void
}

export const TicketCreationFields: FC<TicketCreationFieldsProps> = ({
  configurationError,
  sourceError,
  canCapturePlanningSource,
  areSourcesLoading,
  hasSourceLoadError,
  selectedUnitIds,
  sourcesByUnit,
  unitMap,
  onSourceChange,
}) => (
  <>
    {configurationError && (
      <Alert type='error' showIcon title={configurationError} />
    )}
    {sourceError && <Alert type='error' showIcon title={sourceError} />}
    {canCapturePlanningSource && !configurationError && (
      <Space orientation='vertical'>
        {hasSourceLoadError && (
          <Alert
            type='error'
            showIcon
            title='Could not load completed NII assessments'
            description='Check the connection and try again.'
          />
        )}
        {!areSourcesLoading &&
          !hasSourceLoadError &&
          selectedUnitIds.some(
            (unitId) => (sourcesByUnit.get(unitId) ?? []).length === 0
          ) && (
            <Alert
              type='info'
              showIcon
              title='No completed NII assessments are available for one or more selected assets in this campaign.'
            />
          )}
        {selectedUnitIds.map((unitId) => {
          const sources = sourcesByUnit.get(unitId) ?? []
          const unitName = unitMap.get(unitId)?.data.name || unitId
          return (
            <Form.Item
              key={unitId}
              name={['sourceIds', unitId]}
              label={`Completed NII assessment for ${unitName}`}
              rules={[
                {
                  required: true,
                  message: 'Please select a completed NII assessment',
                },
              ]}>
              <Select
                aria-label={`Completed NII assessment for ${unitName}`}
                loading={areSourcesLoading}
                disabled={
                  areSourcesLoading || hasSourceLoadError || sources.length < 2
                }
                placeholder='Select an assessment'
                onChange={onSourceChange}
                options={sources.map((ticket) => ({
                  value: ticket.id,
                  label: `${ticket.title} · v${ticket.version} · ${ticket.id}`,
                }))}
                notFoundContent={
                  areSourcesLoading
                    ? 'Loading completed assessments…'
                    : 'No completed assessments available'
                }
              />
            </Form.Item>
          )
        })}
      </Space>
    )}
  </>
)
